/* eslint-disable eqeqeq */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllFRDevicesByLocDD,
  useGetAllLocationsDropdown,
  useGetAllSystemDetailCodesDropdown,
} from "../../hooks/common/query";
import { formattedDate, mapToOptions } from "../../lib/functions";
import debounce from "lodash.debounce";
import {
  DEVICE_TYPES,
  EXPORT_PAGE_SIZE,
  SYSTEM_DETAIL_CODES,
} from "../../constants/common";
import { useGetAllFRAccessLogsReport } from "../../hooks/report/query";
import {
  FRAccessLog,
  ExportFRAccessLogsReportForExcelPDFBody,
} from "../../hooks/report/interface";
import { Button } from "../button";

interface SearchAccessLogReportCardProps {
  setAllLogs: Dispatch<SetStateAction<FRAccessLog[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportFRAccessLogsReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchAccessLogReportCardSchema = Yup.object({
  fullName: Yup.string(),
  fromDate: Yup.string(),
  toDate: Yup.string(),
  identity: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
  device: Yup.string(),
});

const initialState = {
  fullName: "",
  fromDate: "",
  toDate: "",
  identity: "",
  building: "",
  device: "",
};

const SearchAccessLogReportCard = ({
  setAllLogs,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchAccessLogReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchAccessLogReportCardSchema, {
    defaultValues: {
      fromDate: formattedDate(new Date()),
      toDate: formattedDate(new Date()),
      // location:
      //   user?.n_RoleID !== USER_ROLES_IDS.ADMIN
      //     ? user?.n_LocationID?.toString()
      //       ? user?.n_LocationID?.toString()
      //       : ""
      //     : "",
      location: "",
    },
  });

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");
  const [fullName, setFullName] = useState("");

  const { data: allIdentities } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.IDENTITIES,
  });

  const identities = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allIdentities),
    [allIdentities]
  );

  const location = watch("location");
  const building = watch("building");
  const device = watch("device");
  const identity = watch("identity");
  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  const { data: allLocations } = useGetAllLocationsDropdown();

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  const { data: allFRDevicesByLocDD } = useGetAllFRDevicesByLocDD({
    encryptedLocationID: location ? location?.toString() : "",
    n_BuildingID: building ? Number(building) : 0,
    encryptedFRDeviceTypeID: DEVICE_TYPES.FR?.toString(),
  });

  useEffect(() => {
    setPage(1);
  }, [
    fullName,
    identity,
    device,
    fromDate,
    toDate,
    building,
    location,
    setPage,
  ]);

  const frDevicesDD = useMemo(
    () =>
      mapToOptions(
        "s_DeviceName_En",
        "s_DeviceName_Ar",
        "n_DeviceID",
        allFRDevicesByLocDD
      ),
    [allFRDevicesByLocDD]
  );

  const { data: accessLogs } = useGetAllFRAccessLogsReport({
    pageSize: pageSize,
    pageNumber: page,
    search: search,
    encryptedLocationID: location ? location?.toString() : "",
    n_BuildingID: building ? Number(building) : 0,
    encryptedDeviceID: device ? device?.toString() : "",
    s_Name: fullName,
    n_IdentityType: identity ? Number(identity) : 0,
    dt_FromDate: fromDate,
    dt_ToDate: toDate,
  });

  useEffect(() => {
    if (accessLogs) {
      setAllLogs(accessLogs.data || []);
      setPages(accessLogs.pagination.pages || 1);
      setCount(accessLogs.pagination.count || 0);
    }
  }, [setAllLogs, setPages, accessLogs, setCount]);

  useEffect(() => {
    setExportBody({
      pageSize: EXPORT_PAGE_SIZE,
      pageNumber: page,
      search: search,
      encryptedLocationID: location ? location?.toString() : "",
      n_BuildingID: building ? Number(building) : 0,
      encryptedDeviceID: device ? device?.toString() : "",
      s_Name: fullName,
      n_IdentityType: identity ? Number(identity) : 0,
      dt_FromDate: fromDate,
      dt_ToDate: toDate,
    });
  }, [
    building,
    device,
    fromDate,
    fullName,
    identity,
    location,
    page,
    search,
    setExportBody,
    toDate,
  ]);

  const debouncedSetFullName = useMemo(
    () => debounce((value: string) => setFullName(value), 500),
    []
  );

  const handleFullNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSetFullName(event.target.value);
    },
    [debouncedSetFullName]
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="fullName"
                control={control}
                render={({ field: { name, value } }) => (
                  <TextInput
                    label={t("fullName")}
                    onChange={handleFullNameChange}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="fromDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("fromDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="toDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("toDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="identity"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={identities}
                    value={
                      identities?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("identity")}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="device"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <ComboboxInput
                      options={frDevicesDD}
                      value={
                        frDevicesDD?.find((item) => item.value === value)
                          ?.labelEn || ""
                      }
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      label={t("device")}
                    />
                  );
                }}
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchAccessLogReportCard);
